/**
 * Runtime zod validator for SchoolRecord arrays decoded from JSON.
 *
 * What: `parseSchoolRecordList` narrows an unknown value (typically the result
 *       of `JSON.parse` on a prerendered `<script type="application/json">` tag)
 *       into a typed `readonly SchoolRecord[]`, returning null if the shape
 *       doesn't match.
 * Why:  The prerender pipeline writes this JSON from a trusted build-time source,
 *       but consumers want a narrowing guard stronger than `Array.isArray` so
 *       downstream components can trust field access without defensive casts.
 * When: Update when `SchoolRecord` in `schoolData.ts` gains/removes fields.
 */
import { z } from 'zod';

import type { SchoolRecord } from './schoolData.js';

const schoolRecordSchema = z.object({
  schoolName: z.string(),
  displayName: z.string(),
  slug: z.string(),
  abbreviation: z.string(),
  routePath: z.string(),
  websiteUrl: z.string(),
  address: z.string(),
  postalCode: z.string(),
  phone: z.string(),
  email: z.string(),
  nearestMrt: z.string(),
  busInfo: z.string(),
  zone: z.string(),
  mainLevelCode: z.string(),
  typeCode: z.string(),
  natureCode: z.string(),
  sessionCode: z.string(),
  sapInd: z.boolean(),
  autonomousInd: z.boolean(),
  giftedInd: z.boolean(),
  ipInd: z.boolean(),
  servesPrimary: z.boolean(),
  servesSecondary: z.boolean(),
}) satisfies z.ZodType<SchoolRecord>;

const schoolRecordListSchema = z.array(schoolRecordSchema);

export function parseSchoolRecordList(
  value: unknown
): readonly SchoolRecord[] | null {
  const result = schoolRecordListSchema.safeParse(value);
  return result.success ? result.data : null;
}
